import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../services/apiClient';

// Hours vs 8 (Pat, Oct 7 2026): "how many hours above or below 8 hrs each person has, and a running
// average." QuickBooks Time timesheets, by person: every weekday worked held against 8 hours; Saturday
// and Sunday hours are all extra; Lunch Break is left out; today shows but counts once the day is done.
const btn = { padding: '6px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12.5, fontWeight: 700, color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit' };
const on = { background: '#1e3a5f', borderColor: '#1e3a5f', color: '#fff' };
const pad = n => String(n).padStart(2, '0');
const ymd = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (s, n) => { const d = new Date(s + 'T12:00:00'); d.setDate(d.getDate() + n); return ymd(d); };
const monday = s => { const d = new Date(s + 'T12:00:00'); const w = (d.getDay() + 6) % 7; d.setDate(d.getDate() - w); return ymd(d); };
const h = n => (n == null ? '—' : `${Math.round(n * 10) / 10}`);
const signed = n => (n == null ? '—' : `${n > 0 ? '+' : n < 0 ? '−' : '±'}${h(Math.abs(n))}`);
const tone = n => (n == null ? '#6b7280' : n > 0.05 ? '#15803d' : n < -0.05 ? '#b91c1c' : '#374151');
const dayName = s => new Date(s + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const GROUPS = {
  all:    { label: 'Everyone', roles: null },
  field:  { label: 'Field', roles: ['Worker', 'FieldSupervisor'] },
  shop:   { label: 'Shop', roles: ['ShopWorker'] },
  office: { label: 'Office & PMs', roles: ['ProjectManager', 'OfficeStaff', 'Accounting', 'Executive', 'Admin'] },
};
const ROLE_WORD = { Worker: 'Field', FieldSupervisor: 'Field Supervisor', ShopWorker: 'Shop', ProjectManager: 'PM', OfficeStaff: 'Office', Accounting: 'Office', Executive: 'Executive' };

function ranges(today) {
  const mon = monday(today);
  return {
    week:  { label: 'This week', start: mon, end: today },
    last:  { label: 'Last week', start: addDays(mon, -7), end: addDays(mon, -1) },
    two:   { label: 'Last 2 weeks', start: addDays(today, -13), end: today },
    month: { label: 'This month', start: today.slice(0, 8) + '01', end: today },
    d30:   { label: 'Last 30 days', start: addDays(today, -29), end: today },
  };
}

export default function HoursVsEightPanel() {
  const today = ymd(new Date());
  const R = ranges(today);
  const [pick, setPick] = useState(() => { try { return localStorage.getItem('titan_hours8_range') || 'two'; } catch { return 'two'; } });
  const [custom, setCustom] = useState({ start: R.two.start, end: today });
  const [group, setGroup] = useState('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ k: 'over_under', dir: -1 });
  const [openId, setOpenId] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const range = pick === 'custom' ? custom : (R[pick] || R.two);

  useEffect(() => {
    setData(null); setErr('');
    apiFetch(`/hours-vs-eight?start=${range.start}&end=${range.end}`).then(setData).catch(e => setErr(e?.error || 'Could not read QuickBooks Time.'));
  }, [range.start, range.end]);
  const choose = k => { setPick(k); try { localStorage.setItem('titan_hours8_range', k); } catch {} };

  const rows = useMemo(() => {
    const roles = GROUPS[group].roles;
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const list = (data?.people || []).filter(p => (!roles || roles.includes(p.role)) && words.every(w => `${p.name} ${ROLE_WORD[p.role] || p.role || ''}`.toLowerCase().includes(w)));
    const v = p => (sort.k === 'name' ? p.name.toLowerCase() : p[sort.k] == null ? -1e9 : p[sort.k]);
    return list.sort((a, b) => (v(a) < v(b) ? -1 : v(a) > v(b) ? 1 : 0) * sort.dir);
  }, [data, group, q, sort]);
  const totals = useMemo(() => {
    const counted = rows.filter(p => p.days_worked);
    const wk = counted.reduce((n, p) => n + p.weekdays, 0);
    const wkHours = counted.reduce((n, p) => n + (p.avg_hours || 0) * p.weekdays, 0);
    return { over: counted.reduce((n, p) => n + p.over_under, 0), hours: counted.reduce((n, p) => n + p.total_hours, 0), avg: wk ? wkHours / wk : null,
      above: counted.filter(p => p.over_under > 0.05).length, below: counted.filter(p => p.over_under < -0.05).length };
  }, [rows]);
  const sortBy = k => setSort(s => (s.k === k ? { k, dir: -s.dir } : { k, dir: k === 'name' ? 1 : -1 }));
  const th = (k, label, align = 'right') => (
    <th onClick={() => sortBy(k)} style={{ textAlign: align, padding: '8px 10px', fontSize: 11.5, fontWeight: 800, color: sort.k === k ? '#1e3a5f' : '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, cursor: 'pointer', whiteSpace: 'nowrap', borderBottom: '2px solid #e5e7eb' }}>
      {label}{sort.k === k ? (sort.dir > 0 ? ' ▲' : ' ▼') : ''}
    </th>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {Object.entries(R).map(([k, r]) => <button key={k} onClick={() => choose(k)} style={{ ...btn, ...(pick === k ? on : null) }}>{r.label}</button>)}
        <button onClick={() => choose('custom')} style={{ ...btn, ...(pick === 'custom' ? on : null) }}>Pick dates</button>
        {pick === 'custom' && <>
          <input type="date" value={custom.start} max={custom.end} onChange={e => e.target.value && setCustom(c => ({ ...c, start: e.target.value }))} style={btn} />
          <span style={{ color: '#6b7280' }}>to</span>
          <input type="date" value={custom.end} min={custom.start} max={today} onChange={e => e.target.value && setCustom(c => ({ ...c, end: e.target.value }))} style={btn} />
        </>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {Object.entries(GROUPS).map(([k, g]) => <button key={k} onClick={() => setGroup(k)} style={{ ...btn, padding: '4px 10px', fontSize: 12, ...(group === k ? on : null) }}>{g.label}</button>)}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Find a person…" style={{ ...btn, fontWeight: 500, width: 200, cursor: 'text' }} />
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: '#6b7280' }}>{dayName(range.start)} – {dayName(range.end)} · weekdays against 8 h · weekend hours all extra · lunch left out · today counts tomorrow</span>
      </div>

      {err && <div style={{ fontSize: 13, color: '#dc2626', fontWeight: 700, marginBottom: 10 }}>{err}</div>}
      {!data && !err && <div style={{ color: '#6b7280', padding: 20 }}>Reading QuickBooks Time…</div>}
      {data && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            {[
              ['People', rows.length, '#111827'],
              ['Hours clocked', h(totals.hours), '#111827'],
              ['Over / under 8, all together', signed(totals.over), tone(totals.over)],
              ['Average weekday', totals.avg == null ? '—' : `${h(totals.avg)} h`, tone(totals.avg == null ? null : totals.avg - 8)],
              ['Above 8 · below 8', `${totals.above} · ${totals.below}`, '#111827'],
            ].map(([l, v, c]) => (
              <div key={l} style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', minWidth: 130 }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: c }}>{v}</div>
                <div style={{ fontSize: 11.5, color: '#6b7280', fontWeight: 600 }}>{l}</div>
              </div>
            ))}
          </div>
          {!rows.length ? <div style={{ color: '#9ca3af', padding: 20 }}>Nobody on the timesheets for that.</div> : (
            <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                <thead><tr>{th('name', 'Person', 'left')}{th('days_worked', 'Days')}{th('total_hours', 'Hours')}{th('over_under', 'Over / under 8')}{th('avg_hours', 'Running avg / weekday')}<th style={{ borderBottom: '2px solid #e5e7eb', padding: '8px 10px', fontSize: 11.5, color: '#6b7280', textAlign: 'right', textTransform: 'uppercase' }}>Today</th></tr></thead>
                <tbody>
                  {rows.map(p => {
                    const id = p.user_id || p.name;
                    const open = openId === id;
                    return (
                      <React.Fragment key={id}>
                        <tr onClick={() => setOpenId(open ? null : id)} style={{ cursor: 'pointer', background: open ? '#f8fafc' : undefined, borderTop: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '9px 10px' }}><b>{p.name}</b> <span style={{ fontSize: 12, color: '#9ca3af' }}>{ROLE_WORD[p.role] || p.role || ''}</span> <span style={{ color: '#9ca3af' }}>{open ? '▾' : '▸'}</span></td>
                          <td style={{ padding: '9px 10px', textAlign: 'right' }}>{p.days_worked}{p.weekend_days ? <span style={{ fontSize: 11.5, color: '#9ca3af' }}> ({p.weekend_days} wkend)</span> : null}</td>
                          <td style={{ padding: '9px 10px', textAlign: 'right' }}>{h(p.total_hours)}</td>
                          <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 800, color: tone(p.over_under) }}>{p.days_worked ? `${signed(p.over_under)} h` : '—'}</td>
                          <td style={{ padding: '9px 10px', textAlign: 'right' }}>{p.avg_hours == null ? '—' : <><b>{h(p.avg_hours)} h</b> <span style={{ fontSize: 12, fontWeight: 700, color: tone(p.avg_over_under) }}>({signed(p.avg_over_under)})</span></>}</td>
                          <td style={{ padding: '9px 10px', textAlign: 'right', fontSize: 12.5, color: '#6b7280' }}>{p.today ? <>{h(p.today.hours)} h{p.today.on_clock ? <span style={{ color: '#15803d', fontWeight: 700 }}> ● on</span> : ''}</> : ''}</td>
                        </tr>
                        {open && (
                          <tr><td colSpan={6} style={{ padding: '4px 10px 14px', background: '#f8fafc' }}>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {p.days.map(d => (
                                <div key={d.date} title={d.jobs.join(', ')} style={{ background: '#fff', border: `1px solid ${d.in_progress ? '#bfdbfe' : '#e5e7eb'}`, borderTop: `4px solid ${d.in_progress ? '#93c5fd' : tone(d.diff)}`, borderRadius: 8, padding: '6px 9px', minWidth: 96 }}>
                                  <div style={{ fontSize: 11.5, fontWeight: 700, color: '#6b7280' }}>{dayName(d.date)}{d.weekend ? ' · wkend' : ''}</div>
                                  <div style={{ fontSize: 14, fontWeight: 800 }}>{h(d.hours)} h <span style={{ fontSize: 12, color: d.in_progress ? '#2563eb' : tone(d.diff) }}>{d.in_progress ? 'so far' : signed(d.diff)}</span></div>
                                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{d.running_avg != null ? `avg ${h(d.running_avg)} h` : ''}{d.jobs.length ? ` · ${d.jobs.slice(0, 2).join(', ')}` : ''}</div>
                                </div>
                              ))}
                            </div>
                          </td></tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
